import BusinessTransform from "@/assets/business_transform.jpg";

const stats = [
  { icon: 'ri-group-line', value: '4', label: 'Expert Speakers', color: 'text-green-700' },
  { icon: 'ri-time-line', value: '2h', label: 'Intensive Learning', color: 'text-amber-600' },
  { icon: 'ri-tools-line', value: '1', label: 'Live AI Demos', color: 'text-green-700' },
  { icon: 'ri-global-line', value: '100%', label: 'Online & Free', color: 'text-amber-600' },
];

const whyItems = [
  'Increasing productivity across all business functions',
  'Reducing operational costs by up to 40%',
  'Scaling faster with automated workflows',
  'Outperforming competitors who delay adoption',
];

const audiences = [
  { icon: 'ri-store-2-line', title: 'SMEs & Business Owners', desc: 'Streamline operations and cut costs with AI tools built for small businesses.', bg: 'from-green-50 to-green-100', border: 'border-green-200', iconBg: 'bg-green-100', iconColor: 'text-green-700' },
  { icon: 'ri-rocket-line', title: 'Startup Founders', desc: 'Build lean, AI-powered startups that scale without massive headcount.', bg: 'from-amber-50 to-amber-100', border: 'border-amber-200', iconBg: 'bg-amber-100', iconColor: 'text-amber-600' },
  { icon: 'ri-lightbulb-line', title: 'Entrepreneurs & Freelancers', desc: 'Automate repetitive tasks and focus on high-value creative work.', bg: 'from-green-50 to-emerald-100', border: 'border-emerald-200', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-700' },
  { icon: 'ri-line-chart-line', title: 'Business Growth Leaders', desc: 'Discover AI-driven strategies to unlock new revenue streams and markets.', bg: 'from-yellow-50 to-amber-100', border: 'border-yellow-200', iconBg: 'bg-yellow-100', iconColor: 'text-yellow-700' },
];

const EventOverview = () => {
  return (
    <section id="overview" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-semibold uppercase tracking-widest mb-4">
            Event Overview
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4">
            Transform Your Business
            <span className="block bg-gradient-to-r from-green-700 to-green-600 bg-clip-text text-transparent">
              with AI — In Just 2 Hours
            </span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
            Artificial Intelligence is now a business necessity — not an option. Join us for a power-packed summit designed specifically for SMEs and startups ready to lead.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-20">
          {stats.map((s) => (
            <div key={s.label} className="flex flex-col items-center text-center p-6 rounded-2xl bg-gray-50 border border-gray-100 hover:-translate-y-1 transition-transform duration-200">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-gray-200 mb-3">
                <i className={`${s.icon} text-xl ${s.color}`}></i>
              </div>
              <span className={`text-3xl font-extrabold ${s.color} mb-1`}>{s.value}</span>
              <span className="text-gray-500 text-sm font-medium">{s.label}</span>
            </div>
          ))}
        </div>

        {/* Why This Matters */}
        <div className="flex flex-col lg:flex-row gap-12 items-center mb-20">
          <div className="flex-1">
            <img
              src={BusinessTransform}
              alt="AI Business Transformation"
              className="w-full h-[320px] md:h-[400px] object-cover object-top rounded-2xl"
            />
          </div>
          <div className="flex-1">
            <span className="inline-block px-3 py-1 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-semibold uppercase tracking-widest mb-4">
              Why This Summit Matters
            </span>
            <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4 leading-tight">
              Early Adopters Will Dominate Their Industries
            </h3>
            <p className="text-gray-500 mb-6 leading-relaxed">
              Businesses using AI today are pulling ahead at an unprecedented pace. The gap between AI-powered businesses and those that wait is widening every month.
            </p>
            <ul className="space-y-3 mb-8">
              {whyItems.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-green-100 mt-0.5 shrink-0">
                    <i className="ri-check-line text-green-700 text-xs"></i>
                  </div>
                  <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <blockquote className="border-l-4 border-green-600 pl-4 italic text-gray-600 text-base">
              "The question is not whether AI will transform your industry — it's whether you'll lead that transformation or be left behind."
            </blockquote>
          </div>
        </div>

        {/* Who Should Attend */}
        <div>
          <div className="text-center mb-10">
            <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2">Who Should Attend</h3>
            <p className="text-gray-500">This summit is built for action-takers ready to leverage AI for real business results.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {audiences.map((a) => (
              <div
                key={a.title}
                className={`p-6 rounded-2xl bg-gradient-to-br ${a.bg} border ${a.border} hover:-translate-y-1 transition-transform duration-200 cursor-default`}
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${a.iconBg} mb-4`}>
                  <i className={`${a.icon} text-xl ${a.iconColor}`}></i>
                </div>
                <h4 className="font-bold text-gray-900 text-base mb-2">{a.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventOverview;
